import { Input } from "@volca/ui/components/ui/input";
import { Label } from "@volca/ui/components/ui/label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { SpreadsheetRegionDraft } from "./spreadsheet-region";
import type { RegionLimitFieldsState } from "./use-region-limit-fields";
import { formatColumnLetter } from "./utils";

interface RegionLimitFieldsProps {
  draft: SpreadsheetRegionDraft;
  fields: Pick<
    RegionLimitFieldsState,
    | "limitFieldText"
    | "isColumnFieldValid"
    | "changeRowLimit"
    | "changeColumnLimit"
  >;
}

export const RegionLimitFields = ({
  draft,
  fields: {
    limitFieldText,
    isColumnFieldValid,
    changeRowLimit,
    changeColumnLimit,
  },
}: RegionLimitFieldsProps) => (
  <Stack className="w-full" align="start" gap={2}>
    <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-4">
      {(
        [
          ["header_row", "Header row"],
          ["record_end_row", "Last record row"],
        ] as const
      ).map(([key, label]) => (
        <Stack align="start" key={key} gap={1}>
          <Label htmlFor={`region-${key}`}>{label}</Label>
          <Input
            id={`region-${key}`}
            type="number"
            min={1}
            aria-invalid={
              limitFieldText[key] !== null &&
              !/^\d+$/.test(limitFieldText[key] ?? "")
            }
            value={limitFieldText[key] ?? draft[key]}
            onChange={(event) => changeRowLimit(key, event.target.value)}
          />
        </Stack>
      ))}
      {/* Columns are entered as letters, because letters are what the
        grid labels them with and what every formula on the sheet
        refers to. Asking for "21" when the header reads "U" makes the
        user do the conversion the spreadsheet already did. */}
      {(
        [
          ["start_col", "First column"],
          ["end_col", "Last column"],
        ] as const
      ).map(([key, label]) => (
        <Stack align="start" key={key} gap={1}>
          <Label htmlFor={`region-${key}`}>{label}</Label>
          <Input
            id={`region-${key}`}
            inputMode="text"
            autoCapitalize="characters"
            spellCheck={false}
            aria-invalid={
              limitFieldText[key] !== null && !isColumnFieldValid[key]
            }
            value={limitFieldText[key] ?? formatColumnLetter(draft[key] - 1)}
            onChange={(event) => changeColumnLimit(key, event.target.value)}
          />
        </Stack>
      ))}
    </div>

    {/* The same A1 range Excel would show for this table. */}
    <Text variant="caption" tone="muted">
      {formatColumnLetter(draft.start_col - 1)}
      {draft.header_row}:{formatColumnLetter(draft.end_col - 1)}
      {draft.record_end_row} &middot;{" "}
      {draft.record_end_row - draft.record_start_row + 1} records &middot;{" "}
      {draft.end_col - draft.start_col + 1} columns
    </Text>
  </Stack>
);
